import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from './alert-dialog';
import { Button } from './button';
import { AlertCircle, Clock } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

interface SessionTimeoutDialogProps {
  open: boolean;
  onClose: () => void;
}

export function SessionTimeoutDialog({ open, onClose }: SessionTimeoutDialogProps) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { clearSessionTimeout, refreshSession, userType } = useAuth();
  const [countdown, setCountdown] = useState(60); // 60 seconds countdown
  const [isExtending, setIsExtending] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (!open) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleLoginAgain();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open]);

  // Reset countdown when dialog opens
  useEffect(() => {
    if (open) {
      setCountdown(60);
    }
  }, [open]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      await refreshSession();
      clearSessionTimeout();
      onClose();
    } catch (error) {
      // If session refresh fails, redirect to login
      handleLoginAgain();
    } finally {
      setIsExtending(false);
    }
  };

  const handleLoginAgain = () => {
    clearSessionTimeout();
    onClose();
    // Navigate to appropriate login page based on user type
    if (userType === 'hr') {
      navigate('/auth/hr-login');
    } else {
      navigate('/auth/magic-link');
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(open: boolean) => !open && onClose()}>
      <AlertDialogContent 
        className="sm:max-w-md" 
        data-testid="session-timeout-dialog"
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <AlertDialogTitle className="text-left">
              {t('errors.sessionExpired')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-left">
            {t('errors.sessionExpiredDesc')}
          </AlertDialogDescription>
          
          <div className="flex items-center justify-center gap-2 py-4">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t('errors.autoRedirectCountdown', 'Auto-redirect in {{seconds}} seconds', { 
                seconds: countdown 
              })}
            </span>
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={handleExtendSession}
            disabled={isExtending}
            className="w-full sm:w-auto"
          >
            {isExtending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                {t('extending', 'Extending...')}
              </>
            ) : (
              t('extendSession', 'Extend Session')
            )}
          </Button>
          <Button 
            onClick={handleLoginAgain}
            className="w-full sm:w-auto"
          >
            {t('loginAgain', 'Login Again')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}